// Row identity for the shared paged tables' tick boxes.
//
// TanStack keys row selection by row id, and its default id is the row's position on the page. The
// paged tables refetch underneath the user (on an interval, and after every mutation), so a refetch
// that re-sorted or dropped a row left the tick on the same slot — now holding a different part — and
// a bulk action like "Change Warehouse Location" then moved a part nobody ticked.
//
// Keying by the record's own id keeps a tick on the record it was put on. Rows only get their id when
// every row on the page has a distinct one: a table of records without ids (or with repeats) keeps the
// positional key it always had, rather than merging rows that share an id into one selection.
export type RowIdGetter<T> = (row: T, index: number) => string;

export function buildRowIdGetter<T>(rows: readonly T[]): RowIdGetter<T> {
  const ids = rows.map(readRowId);
  const allDistinct = ids.every((id) => id !== null) && new Set(ids).size === ids.length;

  return allDistinct
    ? (row, index) => readRowId(row) ?? index.toString()
    : (_row, index) => index.toString();
}

// True when a tick points at a row the page no longer has — the part moved off a filtered list,
// sold, or the user paged on. The caller clears the whole selection, as it did before, so a bulk
// action never runs against a tick the user can't see.
export function hasStaleSelection<T>(
  selection: Record<string, boolean>,
  rows: readonly T[],
  getRowId: RowIdGetter<T>
): boolean {
  const rowIds = new Set(rows.map((row, index) => getRowId(row, index)));
  return Object.keys(selection).some((key) => !rowIds.has(key));
}

function readRowId(row: unknown): string | null {
  if (row === null || typeof row !== "object") return null;

  const id = (row as { id?: unknown }).id;
  if (typeof id === "string" && id !== "") return id;
  if (typeof id === "number") return id.toString();
  return null;
}
